import { useState } from 'react';
import { Map as MapIcon, Copy, Check, ShieldCheck, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { joinAddress } from '../../../utils/join-address';
import { formatAgo, formatUptime, prettyMap } from '../../../utils/overview-format';
import type { SteamOverview } from './types';

const HERO_SHADE =
  'linear-gradient(90deg, rgba(11,11,16,0.97) 0%, rgba(11,11,16,0.86) 36%, rgba(11,11,16,0.35) 68%, rgba(11,11,16,0.6) 100%), linear-gradient(0deg, rgba(11,11,16,0.75) 0%, transparent 45%)';

const TONE = {
  online: { dot: 'bg-online', ring: 'shadow-[0_0_0_4px_rgba(55,217,160,0.15)]' },
  warn: { dot: 'bg-warn', ring: 'shadow-[0_0_0_4px_rgba(245,184,65,0.15)]' },
  muted: { dot: 'bg-fg-muted', ring: '' },
};

interface Props {
  overview: SteamOverview;
  server: { status: string; started_at?: string | null; port?: number | null };
  firewall: any;
  playerNames: string[];
}

/**
 * „Bühne" der Steam-Übersicht: Spielbild mit dem Wichtigsten darauf — ist der
 * Server bereit, wie komme ich rein, wer ist online (Entwurf A, 2026-09-14).
 */
export const SteamHero = ({ overview, server, firewall, playerNames }: Props) => {
  const images = [overview.game.heroImage, overview.game.fallbackImage].filter(Boolean) as string[];
  const [imgIdx, setImgIdx] = useState(0);
  const [copied, setCopied] = useState(false);

  const online = server.status === 'online';
  const port = firewall?.required?.[0]?.port ?? server.port;
  const address = joinAddress(window.location.hostname, port, firewall?.defaultPort);
  const saved = formatAgo(overview.savedAt);
  const map = prettyMap(overview.map || overview.query?.map);

  const state = !online
    ? { label: 'Server ist gestoppt', tone: 'muted' as const, sub: 'Starte ihn oben rechts — hier steht dann, sobald er bereit ist.' }
    : overview.rcon === 'waiting'
      ? { label: 'Server startet …', tone: 'warn' as const, sub: 'Der Server lädt noch, das dauert je nach Spiel ein paar Minuten.' }
      : {
          label: overview.rcon === 'ready' ? 'Bereit zum Spielen' : 'Server läuft',
          tone: 'online' as const,
          sub: [`Online seit ${formatUptime(server.started_at)}`, saved && `Welt zuletzt gespeichert ${saved}`].filter(Boolean).join(' · '),
        };

  const count = playerNames.length || overview.query?.players || 0;
  const max = overview.maxPlayers || overview.query?.maxPlayers || null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Kopieren nicht möglich — Adresse manuell markieren.');
    }
  };

  const hint = overview.joinHint;
  const hintParts = hint ? hint.text.split(/(\{key\}|\{command\})/) : [];

  return (
    <div className="relative rounded-2xl overflow-hidden border border-line bg-surface">
      {images[imgIdx] && (
        <img
          src={images[imgIdx]}
          alt=""
          onError={() => setImgIdx((i) => i + 1)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: 'center 30%' }}
        />
      )}
      <div className="absolute inset-0" style={{ background: HERO_SHADE }} />
      <div className="absolute inset-x-0 top-0 h-px gm-powerline" />

      <div className="relative flex flex-col md:flex-row justify-between gap-6 px-6 py-6 md:px-8 md:py-7 min-h-[272px]">
        <div className="flex flex-col justify-between gap-6 max-w-[600px]">
          <div>
            <div className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">
              <span>{overview.game.name}</span>
              {map && (
                <>
                  <span className="w-[3px] h-[3px] rounded-full bg-fg-muted" />
                  <span className="inline-flex items-center gap-1.5 normal-case tracking-normal text-xs text-fg">
                    <MapIcon size={13} /> {map}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3.5 mt-4">
              <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', TONE[state.tone].dot, TONE[state.tone].ring, state.tone !== 'muted' && 'gm-live-dot')} />
              <h2 className="font-display text-3xl md:text-[34px] font-semibold tracking-tight leading-tight text-fg">{state.label}</h2>
            </div>
            <p className="mt-2 text-sm text-[#B4B4C2]">{state.sub}</p>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">Beitreten</div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="px-3.5 py-2 rounded-[10px] font-mono text-[15px] text-fg bg-ink/70 border border-line-strong select-all">{address}</code>
              <button
                onClick={copy}
                title="Adresse kopieren"
                className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center bg-ink/70 border border-line-strong text-fg hover:text-ember transition-colors"
              >
                {copied ? <Check size={16} className="text-online" /> : <Copy size={16} />}
              </button>
              {firewall?.checked && (firewall.allOpen ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-online bg-online/10 border border-online/25">
                  <ShieldCheck size={13} /> Ports freigegeben
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs text-warn bg-warn/10 border border-warn/25">
                  <ShieldAlert size={13} /> Ports fehlen — siehe unten
                </span>
              ))}
            </div>
            {hint && (
              <p className="text-xs text-fg-muted">
                {hintParts.map((part, i) =>
                  part === '{key}' ? (
                    <kbd key={i} className="font-mono text-[11px] text-fg px-1.5 py-px rounded-[5px] border border-line-strong">{hint.key}</kbd>
                  ) : part === '{command}' ? (
                    <code key={i} className="font-mono text-fg">{(hint.command || '').replace('{address}', address)}</code>
                  ) : (
                    <span key={i}>{part}</span>
                  ),
                )}
              </p>
            )}
          </div>
        </div>

        {online && (
          <div className="md:self-end flex md:flex-col items-center md:items-end gap-2.5">
            {playerNames.length > 0 && (
              <div className="flex">
                {playerNames.slice(0, 4).map((n, i) => (
                  <div
                    key={n}
                    title={n}
                    className={cn('w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold border-2 border-ink', i > 0 && '-ml-2', i % 2 ? 'text-ember-2 bg-[#2A1D12]' : 'text-ember bg-[#2A1812]')}
                  >
                    {(n.trim()[0] || '?').toUpperCase()}
                  </div>
                ))}
              </div>
            )}
            <div className="font-mono text-[40px] leading-none tracking-tight text-fg">
              {count}
              {max && <span className="text-[22px] text-fg-muted"> / {max}</span>}
            </div>
            <div className="text-[11px] uppercase tracking-[0.08em] font-medium text-fg-muted">Spieler online</div>
          </div>
        )}
      </div>
    </div>
  );
};
